import clsx from 'clsx';
import { CalendarPlus, CalendarClock, Video } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import { api, useMeetings } from '@/api';
import { CalendarSubscribe } from '@/components/CalendarSubscribe';
import { DownloadButton } from '@/components/DownloadButton';
import { QueryPending } from '@/components/QueryState';
import { Status } from '@/components/ui';
import { useContour } from '@/lib/contour';
import { formatDate, formatTime } from '@/lib/format';
import { googleCalendarUrl, icsName, isCanceled, isHeld, recurrenceLabel, splitMeetings } from '@/lib/meetings';
import type { Meeting } from '@/api/types';
import type { ProjectContext } from './ProjectShell';

function Upcoming({ m, google }: { m: Meeting; google: boolean }) {
  const canceled = isCanceled(m);
  const series = recurrenceLabel(m);
  const btn = 'btn-secondary h-7 gap-1.5 px-2.5 text-xs';
  return (
    <li className="rounded-lg p-4 shadow-hair">
      <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
        <CalendarClock className="size-4" /> <span className={clsx(canceled && 'text-muted line-through')}>{m.title}</span>
        {canceled && <Status tone="neutral">Отменена</Status>}
        {series && !canceled && <span className="rounded-full px-2 py-0.5 text-xs font-normal text-muted shadow-hair">{series}</span>}
      </div>
      <div className={clsx('mt-0.5 text-xs text-muted', canceled && 'line-through')}>
        {formatDate(m.at)}, {formatTime(m.at)}
        {m.durationMin ? `, ${m.durationMin} мин` : ''}
      </div>
      {!canceled && m.agenda.length > 0 && (
        <ul className="mt-2 list-disc space-y-0.5 pl-4 text-sm">
          {m.agenda.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      )}
      {!canceled && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {m.link && (
            <a href={m.link} target="_blank" rel="noreferrer" className={btn}>
              <Video className="size-3.5" /> Подключиться
            </a>
          )}
          <DownloadButton className={btn} name={icsName(m)} label="Добавить в календарь" load={() => api.meetingIcs(m.id)}>
            <CalendarPlus className="size-3.5" /> Добавить в календарь
          </DownloadButton>
          {m.seriesId && (
            <DownloadButton className={btn} name={icsName(m, true)} label="Вся серия в календарь" load={() => api.meetingIcs(m.id, true)}>
              Вся серия
            </DownloadButton>
          )}
          {google && (
            <a href={googleCalendarUrl(m)} target="_blank" rel="noreferrer" className={btn}>
              Google Календарь
            </a>
          )}
        </div>
      )}
    </li>
  );
}

/** Meetings: what is planned (with a link and a calendar file) and what was decided on the held ones. */
export function ProjectMeetings() {
  const { project } = useOutletContext<ProjectContext>();
  const q = useMeetings(project.id);
  const meetings = q.data;
  // Google's «add to calendar» link only where the contour allows Google: never in the RU one.
  const google = useContour()?.code === 'main';
  const { upcoming, past } = splitMeetings(meetings ?? []);

  return (
    <>
      <div className="mb-4 flex justify-end">
        <CalendarSubscribe />
      </div>
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
      <section>
        <h2 className="mb-3 text-sm font-semibold">Ближайшие встречи</h2>
        {!meetings ? (
          <QueryPending query={q} />
        ) : upcoming.length === 0 ? (
          <p className="text-sm text-muted">Не запланированы.</p>
        ) : (
          <ul className="space-y-4">
            {upcoming.map((m) => (
              <Upcoming key={m.id} m={m} google={google} />
            ))}
          </ul>
        )}
      </section>
      <section>
        <h2 className="mb-3 text-sm font-semibold">Итоги и решения</h2>
        {past.length === 0 ? (
          <p className="text-sm text-muted">Итоги появятся после первой встречи.</p>
        ) : (
          <ul className="space-y-4">
            {past.map((m) => (
              <li key={m.id}>
                <div className={clsx('flex flex-wrap items-center gap-2 text-sm font-medium', isCanceled(m) && 'text-muted line-through')}>
                  {m.title}
                  {isCanceled(m) && <Status tone="neutral">Отменена</Status>}
                  {isHeld(m) && <Status tone="done">Прошла</Status>}
                </div>
                <div className="text-xs text-muted">{formatDate(m.at)}</div>
                {isCanceled(m) ? null : m.outcome ? (
                  <>
                    <p className="mt-1 text-sm">{m.outcome.summary}</p>
                    <ul className="mt-1.5 space-y-1 text-sm">
                      {m.outcome.decisions.map((d) => (
                        <li key={d} className="flex gap-2">
                          <span className="mt-2 size-1 shrink-0 rounded-full bg-ink" />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="mt-1 text-sm text-muted">Итоги скоро появятся.</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      </div>
    </>
  );
}
